import { computed, ComputedRef, reactive } from "vue";
import axios from "axios";

import useNotify from "@Composables/useNotify";
import type { PaddleCompactCheckoutContent } from "@Composables/usePaddle";
import useTranslation from "@Composables/useTranslation";
import DiscountModel, { type Discount, type DiscountData } from "@Modules/billing/Data/Models/DiscountModel";
import InvoiceModel, { type Invoice, type InvoiceData } from "@Modules/billing/Data/Models/InvoiceModel";
import { ParseServerError, ProcessServerErrors } from "@Modules/shared/Api/ServerErrors";
import type ReplaceableRouter from "@Modules/shared/interfaces/ReplaceableRouter";
import type { SubscriptionData } from "@Modules/subscriptions/Data/Models/SubscriptionModel";
import BILLING_TYPE from "@Constants/billing-type";

/**
 * Query parameters the payment method checkout session endpoint validates.
 */
export interface PaymentMethodSessionParams {
  subscription: string
  subscription_type: string
  billing_type: number
}

/**
 * What the pay endpoint hands back for a single invoice.
 *
 * A paddle invoice comes back as a compact checkout payload, a stripe one as a redirect
 * url to follow instead, so the paddle fields are only really there on the paddle side.
 */
export type InvoicePaymentContent = PaddleCompactCheckoutContent & {
  billing_type?: number | null
  redirect_url?: string | null
}

/**
 * The checkout that settles every unpaid transaction at once: a paddle transaction to
 * open, or a stripe session id to redirect to.
 */
export type UnpaidTransactionContent = PaddleCompactCheckoutContent & {
  billing_type?: number | null
  session_id?: string | null
}

/**
 * What the unpaid transaction endpoint hands back, carrying either the checkout session
 * to open or the reason it could not be created.
 */
export interface UnpaidTransactionSession {
  data?: UnpaidTransactionContent | null
  error?: { message?: string } | null
}

/**
 * The billing slice of the full OrganizationResource.
 */
interface OrganizationBillingData {
  subscriptions?: SubscriptionData[] | null
  unpaid_overage?: number | null
  currency?: string | null
  vat_id?: string | null
}

interface BillingApiState {
  loadingOrganizationBilling: boolean
  couponErrors: Record<string, string>
  vatIdErrors: Record<string, string>
  invoices: Invoice[]
  invoicesReady: boolean
  discount: Discount | null
  orgSubscriptions: SubscriptionData[]
  orgUnpaidOverage: number
  orgCurrency: string | null
  orgVatId: string | null
}

export type BillingApi = ReplaceableRouter<BillingApi, "get" | "post" | "put"> & {
  loadingOrganizationBilling: ComputedRef<boolean>
  couponErrors: ComputedRef<Record<string, string>>
  vatIdErrors: ComputedRef<Record<string, string>>
  invoices: ComputedRef<Invoice[]>
  invoicesReady: ComputedRef<boolean>
  discount: ComputedRef<Discount | null>
  orgSubscriptions: ComputedRef<SubscriptionData[]>
  orgUnpaidOverage: ComputedRef<number>
  orgCurrency: ComputedRef<string | null>
  orgVatId: ComputedRef<string | null>
  fetchInvoices: (orgSlug: string | null) => Promise<boolean>
  fetchPaymentMethodCheckoutSession: (
    orgSlug: string | null,
    params: PaymentMethodSessionParams,
  ) => Promise<PaddleCompactCheckoutContent | null>
  payInvoice: (orgSlug: string | null, invoiceId: string | number) => Promise<InvoicePaymentContent | null>
  fetchHostedInvoiceUrl: (orgSlug: string | null, invoiceId: string | number) => Promise<string | null>
  fetchUnpaidTransactionCheckoutSession: (
    orgSlug: string | null,
    billingType: number,
  ) => Promise<UnpaidTransactionSession | null>
  fetchDiscount: (orgSlug: string | null) => Promise<boolean>
  redeemCoupon: (orgSlug: string | null, code: string) => Promise<boolean>
  updateVatId: (orgSlug: string | null, vatId: string) => Promise<boolean>
  fetchOrganizationBilling: (orgSlug: string) => Promise<boolean>
}

/**
 * Builds the API URL based on whether we are in user or org context
 */
function buildUrl(orgSlug: string | null, path: string): string {
  if (orgSlug) {
    return `/app/api/organization/${orgSlug}/${path}`;
  }

  return `/app/api/settings/${path}`;
}

/**
 * Talks to the invoice and payment method endpoints.
 *
 * The api is context agnostic: pass an org slug for the organization endpoints,
 * or null for the single user endpoints.
 */
export default function BillingApi(): BillingApi {
  const { notifyError } = useNotify();
  const { __ } = useTranslation();

  const state = reactive<BillingApiState>({
    loadingOrganizationBilling: false,
    couponErrors: {},
    vatIdErrors: {},
    invoices: [],
    invoicesReady: false,
    discount: null,
    orgSubscriptions: [],
    orgUnpaidOverage: 0,
    orgCurrency: null,
    orgVatId: null,
  });

  let router: Pick<HttpInterface, "get" | "post" | "put"> = axios;

  /**
   * Each form keeps its own validation errors, so submitting one form never clears or
   * overwrites what another form is still showing.
   */
  type ErrorBag = "couponErrors" | "vatIdErrors";

  function clearErrors(bag: ErrorBag): void {
    Object.keys(state[bag]).forEach((key) => delete state[bag][key]);
  }

  /**
   * Files validation errors under the given bag and reports any other failure. A
   * request with no form behind it passes no bag, and its validation errors are
   * reported like any other failure.
   */
  function setErrors(error: unknown, bag: ErrorBag | null = null): void {
    const { fieldErrors, serverMessage } = ParseServerError(error);

    if (fieldErrors && bag !== null) {
      state[bag] = ProcessServerErrors(fieldErrors);
    } else if (serverMessage) {
      notifyError({ text: serverMessage });
    } else {
      notifyError();
    }
  }

  const that: BillingApi = {
    loadingOrganizationBilling: computed(() => state.loadingOrganizationBilling),
    couponErrors: computed(() => state.couponErrors),
    vatIdErrors: computed(() => state.vatIdErrors),
    invoices: computed(() => state.invoices),
    invoicesReady: computed(() => state.invoicesReady),
    discount: computed(() => state.discount),
    orgSubscriptions: computed(() => state.orgSubscriptions),
    orgUnpaidOverage: computed(() => state.orgUnpaidOverage),
    orgCurrency: computed(() => state.orgCurrency),
    orgVatId: computed(() => state.orgVatId),

    /**
     * Loads the billable's invoices into the `invoices` state.
     */
    async fetchInvoices(orgSlug: string | null): Promise<boolean> {
      state.invoicesReady = false;

      try {
        const response = await router.get<{ data?: InvoiceData[] }>(buildUrl(orgSlug, "invoices"));
        const rows: InvoiceData[] = response.data?.data ?? [];

        state.invoices = rows.map((row) => InvoiceModel(row, orgSlug, __));

        return true;
      } catch {
        notifyError();

        return false;
      } finally {
        state.invoicesReady = true;
      }
    },

    /**
     * Gets a checkout session from the backend so a Paddle payment method update can be opened.
     *
     * Carries no busy flag of its own: the view model tracks the whole payment method
     * update through requestedSubscriptionId.
     */
    async fetchPaymentMethodCheckoutSession(
      orgSlug: string | null,
      params: PaymentMethodSessionParams,
    ): Promise<PaddleCompactCheckoutContent | null> {
      try {
        const response = await router.get<{ data: PaddleCompactCheckoutContent }>(
          buildUrl(orgSlug, "payment-method"),
          { params },
        );

        return response.data?.data ?? null;
      } catch {
        notifyError();

        return null;
      }
    },

    /**
     * Starts the payment of a single unpaid invoice and returns what the checkout needs:
     * a paddle transaction to open, or a stripe url to send the browser to.
     */
    async payInvoice(orgSlug: string | null, invoiceId: string | number): Promise<InvoicePaymentContent | null> {
      try {
        const response = await router.post<{ data: InvoicePaymentContent }>(
          buildUrl(orgSlug, `invoices/${invoiceId}/pay`),
          {},
        );

        return response.data?.data ?? null;
      } catch (error) {
        setErrors(error);

        return null;
      }
    },

    /**
     * Asks where the provider hosts the invoice's own page right now, because the page
     * expires and the stored address may no longer answer.
     */
    async fetchHostedInvoiceUrl(orgSlug: string | null, invoiceId: string | number): Promise<string | null> {
      try {
        const response = await router.get<{ data: { url?: string | null } }>(
          buildUrl(orgSlug, `invoices/${invoiceId}/hosted-url`),
        );

        return response.data?.data?.url ?? null;
      } catch (error) {
        setErrors(error);

        return null;
      }
    },

    /**
     * Gets the checkout session that settles every unpaid transaction at once.
     *
     * The failure is left unreported here because the caller names what could not be
     * created.
     */
    async fetchUnpaidTransactionCheckoutSession(
      orgSlug: string | null,
      billingType: number,
    ): Promise<UnpaidTransactionSession | null> {
      try {
        const response = await router.post<UnpaidTransactionSession>(
          buildUrl(orgSlug, "overage"),
          { billing_type: billingType },
        );

        return response.data ?? null;
      } catch {
        return null;
      }
    },

    /**
     * Loads the discount the billable currently receives into the `discount` state.
     *
     * The endpoint has two ways of saying there is nothing to show: a Paddle billable
     * gets 204, and a billable without a discount gets a 422 naming the coupon. Neither
     * is a failure, so both leave the state empty without reporting anything.
     */
    async fetchDiscount(orgSlug: string | null): Promise<boolean> {
      try {
        const response = await router.get<{ data?: DiscountData }>(
          buildUrl(orgSlug, "payment-method/coupon"),
          { params: { billing_type: BILLING_TYPE.STRIPE } },
        );
        const coupon: DiscountData | null = response.data?.data ?? null;

        state.discount = coupon === null ? null : DiscountModel(coupon);

        return true;
      } catch (error) {
        state.discount = null;

        const { status } = ParseServerError(error);

        if (status === 422) {
          return true;
        }

        notifyError();

        return false;
      }
    },

    /**
     * Redeems a coupon code against the billable, which only a Stripe billable can do.
     */
    async redeemCoupon(orgSlug: string | null, code: string): Promise<boolean> {
      clearErrors("couponErrors");

      try {
        await router.post(buildUrl(orgSlug, "payment-method/coupon"), {
          billing_type: BILLING_TYPE.STRIPE,
          coupon: code,
        });

        return true;
      } catch (error) {
        setErrors(error, "couponErrors");

        return false;
      }
    },

    /**
     * Saves the VAT id the billable's invoices are issued against.
     */
    async updateVatId(orgSlug: string | null, vatId: string): Promise<boolean> {
      clearErrors("vatIdErrors");

      try {
        await router.put(buildUrl(orgSlug, "payment-method/vat-id"), { vat_id: vatId });

        return true;
      } catch (error) {
        setErrors(error, "vatIdErrors");

        return false;
      }
    },

    /**
     * Loads the full organization resource for its billing data.
     *
     * The `/state` payload only carries brief organizations, which have neither
     * subscriptions nor an unpaid overage, so the org page fetches them here.
     */
    async fetchOrganizationBilling(orgSlug: string): Promise<boolean> {
      state.loadingOrganizationBilling = true;

      try {
        const response = await router.get<{ data?: OrganizationBillingData }>(
          `/app/api/organization/${orgSlug}`,
        );
        const organization: OrganizationBillingData = response.data?.data ?? {};

        state.orgSubscriptions = organization.subscriptions ?? [];
        state.orgUnpaidOverage = organization.unpaid_overage ?? 0;
        state.orgCurrency = organization.currency ?? null;
        state.orgVatId = organization.vat_id ?? null;

        return true;
      } catch {
        notifyError();

        return false;
      } finally {
        state.loadingOrganizationBilling = false;
      }
    },

    /**
     * Allows you to swap out the router for a fake one when writing tests
     */
    setRouter(newRouter: Pick<HttpInterface, "get" | "post" | "put">): BillingApi {
      router = newRouter;

      return that;
    },
  };

  return that;
}
